/**
 * Pull requests Rigg is not tracking.
 *
 * Deliberately a banner that opens rather than rows in the list. The task list
 * is "everything in flight for this project", and these are not tasks — putting
 * them inline would mean every count, filter and keyboard shortcut had to learn
 * about a second kind of thing that mostly is not there. Collapsed it costs one
 * line; open it is a list with checkboxes, which is all it ever needed to be.
 *
 * It only appears when there is something to adopt, so the usual state of this
 * component is invisible.
 */

import { useCallback, useEffect, useState } from 'react';
import type { OrphanPr } from '@shared/import.ts';
import { api } from '@/lib/api.ts';
import { cn } from '@/lib/format.ts';
import { Button, useToast } from './ui.tsx';
import { GitHubMark } from './Pulses.tsx';

const DISMISS_KEY = 'rigg:orphan-prs-dismissed';

export function OrphanPrs({ projectId, isAdmin }: { projectId: string; isAdmin: boolean }) {
  const { notify } = useToast();
  const [prs, setPrs] = useState<OrphanPr[]>([]);
  const [failed, setFailed] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  // Per project: adopting everything in one repo says nothing about another.
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(`${DISMISS_KEY}:${projectId}`) === '1',
  );

  const key = (pr: OrphanPr) => `${pr.repo_id}#${pr.number}`;

  const load = useCallback(async () => {
    try {
      const result = await api.orphanPrs(projectId);
      setPrs(result.prs);
      setFailed(result.failed);
    } catch {
      // A repository Rigg cannot reach is not worth a toast on every page load.
      // The banner simply does not appear, which is the same as having nothing
      // to adopt from the reader's point of view.
      setPrs([]);
    }
  }, [projectId]);

  useEffect(() => {
    setDismissed(localStorage.getItem(`${DISMISS_KEY}:${projectId}`) === '1');
    setChosen(new Set());
    setOpen(false);
    void load();
  }, [projectId, load]);

  if (prs.length === 0 || dismissed) return null;

  const toggle = (pr: OrphanPr) =>
    setChosen((was) => {
      const next = new Set(was);
      const id = key(pr);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const dismiss = () => {
    localStorage.setItem(`${DISMISS_KEY}:${projectId}`, '1');
    setDismissed(true);
  };

  const doImport = async () => {
    const wanted = prs.filter((pr) => chosen.has(key(pr)));
    if (wanted.length === 0) return;
    setBusy(true);
    try {
      const result = await api.importPrs(
        projectId,
        wanted.map((pr) => ({ repo_id: pr.repo_id, number: pr.number })),
      );
      const silent = result.imported.filter((one) => !one.commented).length;
      notify(
        `Imported ${result.imported.length} pull request${result.imported.length === 1 ? '' : 's'}`,
        {
          // Whether the invitation actually landed is the thing worth knowing:
          // an import nobody was told about is a task that will never report.
          body: silent
            ? `${silent} could not be commented on — whoever is working on ${silent === 1 ? 'it' : 'them'} has not been told.`
            : 'Each one now carries a comment telling whoever is working on it how to report.',
          tone: silent ? 'bad' : 'ok',
        },
      );
      if (result.skipped.length > 0) {
        notify('Some were skipped', { body: result.skipped.join('; '), tone: 'bad' });
      }
      setChosen(new Set());
      await load();
    } catch (error) {
      notify('Could not import', { body: (error as Error).message, tone: 'bad' });
    } finally {
      setBusy(false);
    }
  };

  return (
    // Tighter on a phone, where this banner sits between the queue heading
    // and the list it is competing with for room: same information, less of
    // the screen spent showing it before the first task row does.
    <div className="mt-2.5 rounded-xl border border-line bg-callout px-3 py-2 sm:mt-4 sm:px-4 sm:py-3">
      <div className="flex items-center gap-2 sm:gap-3">
        <GitHubMark className="size-4 shrink-0 text-soft" />
        <button
          type="button"
          onClick={() => setOpen((was) => !was)}
          className="rg-focus min-w-0 flex-1 cursor-pointer text-left text-[13px] text-body"
        >
          <span className="font-[650] text-t1">
            {prs.length} pull request{prs.length === 1 ? '' : 's'} Rigg is not tracking
          </span>
          {failed.length > 0 ? (
            <span className="text-mute"> · {failed.length} repository could not be read</span>
          ) : null}
          <span className="text-mute"> · {open ? 'hide' : 'show'}</span>
        </button>
        {isAdmin && open ? (
          <Button
            tone="primary"
            size="sm"
            disabled={busy || chosen.size === 0}
            onClick={doImport}
          >
            {busy ? 'Importing…' : `Import ${chosen.size || ''}`.trim()}
          </Button>
        ) : null}
        <Button size="sm" onClick={dismiss} aria-label="Dismiss">
          Dismiss
        </Button>
      </div>

      {open ? (
        <div className="mt-3 flex flex-col gap-1 border-t border-line pt-3">
          {!isAdmin ? (
            <p className="m-0 mb-1 text-[12.5px] text-soft">
              Importing is an admin action — these are here so you know they exist.
            </p>
          ) : null}
          {prs.map((pr) => {
            const id = key(pr);
            return (
              <label
                key={id}
                className={cn(
                  'flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5',
                  'hover:bg-line/40',
                )}
              >
                <input
                  type="checkbox"
                  checked={chosen.has(id)}
                  disabled={!isAdmin || busy}
                  onChange={() => toggle(pr)}
                  className="rg-focus size-3.5 shrink-0 accent-accent"
                />
                <span className="min-w-0 flex-1 truncate text-[13px] text-body">
                  <span className="font-mono text-[11.5px] text-mute">#{pr.number}</span>{' '}
                  {pr.title}
                  {pr.draft ? <span className="text-mute"> · draft</span> : null}
                </span>
                <span className="shrink-0 font-mono text-[11px] text-faint">{pr.repo}</span>
                <a
                  href={pr.url}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(event) => event.stopPropagation()}
                  className="rg-focus shrink-0 text-[11.5px] text-accent hover:underline"
                >
                  open
                </a>
              </label>
            );
          })}
          {failed.length > 0 ? (
            <p className="m-0 mt-2 text-[11.5px] leading-relaxed text-mute">
              Could not read: {failed.join('; ')}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
